import { ReactNode } from "react";
import { Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getSelectedContext } from "@/lib/api";
import { isSubscriptionExpired } from "@/hooks/useBilling";
import { useSubscriptionGateQuery } from "@/hooks/useSubscriptionGate";

/**
 * Per-page guard for settings sub-routes that should be inaccessible
 * while the outlet's subscription is expired. Demo outlets always pass
 * through (no subscription concept). When the subscription is expired
 * the route renders a lock card pointing the user at My Account, where
 * they can pay.
 *
 * This wraps individual routes inside the SettingsLayout — the outer
 * ProtectedRoute already redirects expired admins to /settings/account,
 * but inner-route navigations don't re-run that gate, so this component
 * plugs that hole.
 */
export function RequireSubscription({ children }: { children: ReactNode }) {
  const isDemo = !!getSelectedContext()?.outlet?.is_demo;
  const { data, isPending, isError } = useSubscriptionGateQuery();

  if (isDemo) return <>{children}</>;
  // Fail OPEN while resolving / on transient errors with no cached truth.
  if ((isPending || isError) && data === undefined) return <>{children}</>;
  if (isSubscriptionExpired(data)) return <SubscriptionLockedCard />;
  return <>{children}</>;
}

function SubscriptionLockedCard() {
  const navigate = useNavigate();
  return (
    <Card className="border-dashed bg-muted/30">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold">Subscription required</h3>
          <p className="text-sm text-muted-foreground max-w-md mt-1">
            Renew your subscription to access this setting.
          </p>
        </div>
        <Button size="sm" onClick={() => navigate("/settings/account")}>
          Go to My Account
        </Button>
      </CardContent>
    </Card>
  );
}
